import { addMonths, addYears, startOfMonth } from 'date-fns';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useDateLocale } from './useDateLocale';

type Props = {
  minDate: Date;
  maxDate: Date;
  initDate?: Date;
};

function useDateRange({ minDate, maxDate, initDate }: Props) {
  const { dateLocale } = useDateLocale();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initDate);
  const [selectedRange, setSelectedRange] = useState<DateRange>();
  const [currentMonth, setCurrentMonth] = useState(initDate ?? new Date());
  const [mode, setMode] = useState<'range' | 'month'>('range');

  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();

  const clampDate = (date: Date) =>
    date < minDate ? minDate : date > maxDate ? maxDate : date;

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const next =
        mode === 'range'
          ? addMonths(prev, direction === 'prev' ? -1 : 1)
          : addYears(prev, direction === 'prev' ? -1 : 1);
      return clampDate(next);
    });
  };

  const months = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => {
      const d = new Date(currentMonth.getFullYear(), i, 1);
      return {
        index: i,
        label: new Intl.DateTimeFormat(dateLocale.code, {
          month: 'short',
        }).format(d),
      };
    });
  }, [dateLocale.code, currentMonth]);

  const selectMonth = (monthIndex: number) => {
    const newDate = new Date(currentMonth.getFullYear(), monthIndex, 1);
    setCurrentMonth(newDate);
    setMode('range');
  };

  const canNavigate = useMemo(() => {
    if (mode === 'range') {
      return {
        prev: currentMonth > startOfMonth(minDate),
        next: currentMonth < startOfMonth(maxDate),
      };
    } else {
      const year = currentMonth.getFullYear();
      return {
        prev: year > minYear,
        next: year < maxYear,
      };
    }
  }, [currentMonth, mode, minDate, maxDate, minYear, maxYear]);

  return {
    locale: dateLocale,
    limits: { minDate, maxDate },
    range: { selectedRange, setSelectedRange },
    date: { selectedDate, setSelectedDate },
    calendar: { currentMonth, setCurrentMonth, months },
    view: { mode, setMode, handleMonthChange, selectMonth },
    clampDate,
    canNavigate,
  };
}

export { useDateRange };
